import React, { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { motion } from 'framer-motion'
import { Upload, Download, Smartphone, Image, CheckCircle2, Apple, Pipette, ChevronDown, ChevronUp } from 'lucide-react'
import { RiAndroidLine } from 'react-icons/ri'
import { isColorDark } from '../../utils/functions'
import bannerLogo from '../../assets/images/banner-black-min.png'
import brandLogo from '../../assets/images/brand-logo.png'
import iphoneMockup from '../../assets/images/iphone_mockup.png'
import pixelMockup from '../../assets/images/pixel_phone_mockup.png'
import dancingKitty from '../../assets/images/dancing-kitty-white-min.gif'
import GoogleAd from '../../components/common/GoogleAd'
import { trackImageUpload, trackDownload, trackColorChange, trackDonationClick, trackEmailClick, trackIconGeneration } from '../../components/common/GoogleAnalytics'

const DesktopIconGenerator = ({
  uploadedImage,
  backgroundColor,
  setBackgroundColor,
  iconName,
  setIconName,
  padding,
  setPadding,
  platforms,
  setPlatforms,
  outputFormat,
  setOutputFormat,
  onImageUpload,
  handleGenerate,
  isGenerating,
  showToast,
  setShowToast,
  colorInputRef,
  showBadge,
  setShowBadge,
  badgeText,
  setBadgeText,
  badgeColor,
  setBadgeColor,
  monochromeImage,
  setMonochromeImage,
  onMonochromeImageUpload,
  showMonochrome,
  setShowMonochrome
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [showDownloadModal, setShowDownloadModal] = useState(false)

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]
      onImageUpload(file)
      
      // Track image upload
      trackImageUpload(file.size, file.type)
    }
  }, [onImageUpload])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.svg', '.webp']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024
  })

  // Separate dropzone for the preview area
  const { getRootProps: getPreviewRootProps, getInputProps: getPreviewInputProps, isDragActive: isPreviewDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.svg', '.webp']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024
  })

  // Dropzone for monochrome images
  const onMonochromeDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0]
      onMonochromeImageUpload(file)
      
      // Track monochrome image upload
      trackImageUpload(file.size, file.type)
    }
  }, [onMonochromeImageUpload])

  const { getRootProps: getMonochromeRootProps, getInputProps: getMonochromeInputProps, isDragActive: isMonochromeDragActive } = useDropzone({
    onDrop: onMonochromeDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.svg', '.webp']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024
  })

  const iconTypes = [
    { name: 'Round Icon', style: 'rounded-full', size: 100 },
    { name: 'Squircle', style: 'rounded-3xl', size: 100 },
    { name: 'Legacy Icon', style: 'rounded-lg', size: 100 }
  ]

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-0 select-none">
      <div className="grid lg:grid-cols-3 gap-10 h-[calc(100vh-32px)]">
      {/* Left Column - Tools Area - Fits exactly in screen with padding */}
      <div className="lg:col-span-1 flex flex-col pb-8">
        {/* Combined Upload & Customize Section - Full Height */}
        <div className="bg-white rounded-xl shadow-soft border border-gray-200 flex flex-col overflow-y-auto gap-5 flex-1 overflow-x-hidden">
          <div className="flex items-center space-x-2 pt-5 px-5">
            <Upload className="w-4 h-4 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Upload & Customize</h3>
          </div>

          <div className="max-h-[81vh] p-5 pt-0 w-full gap-5 flex flex-col overflow-y-scroll no-scrollbar">
            {/* Upload Area - More compact */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1"> Foreground Image: </label>
              <motion.div
                {...getRootProps()}
                className={`
                  border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-all duration-300 shadow-sm
                  ${isDragActive 
                    ? 'border-primary-500 bg-primary-50 shadow-md ring-2 ring-primary-200' 
                    : uploadedImage
                      ? 'border-green-400 bg-green-50/50 hover:border-green-500 hover:bg-green-50'
                      : 'border-primary-300 bg-gradient-to-br from-primary-25 to-blue-25 hover:border-primary-500 hover:bg-gradient-to-br hover:from-primary-50 hover:to-blue-50 hover:shadow-md'
                  }
                `}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <input {...getInputProps()} />
                {uploadedImage ? (
                  <div className="flex items-center justify-center space-x-3">
                    <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 ring-2 ring-green-200">
                      <img src={uploadedImage} alt="Uploaded" className="w-full h-full object-cover" />
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span className="text-green-600 font-medium text-xs">Click to change</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-3">
                    <div className="w-8 h-8 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <Upload className="w-5 h-5 text-primary-600" />
                    </div>
                    <div>
                      <div className="text-gray-800 font-semibold text-xs mb-0.5">Drop image here or click to upload</div>
                      <div className="text-xs text-primary-600 font-medium">PNG, JPG, SVG up to 10MB</div>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>

            {/* Padding/Zoom - More compact */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Size: <label className="font-normal">{padding > 0 ? `${padding}% padding` : `${Math.abs(padding)}% zoom`}</label>
              </label>
              <input
                type="range"
                min="-100"
                max="100"
                value={padding}
                onChange={(e) => setPadding(parseInt(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider px-1"
              />
              <div className="flex justify-between text-[10px] text-gray-400 mt-1 px-1">
                <span>Zoom</span>
                <span>Normal</span>
                <span>Padding</span>
              </div>
            </div>

            {/* Background Color - More compact */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Background Color:
              </label>
              <div className="relative h-10">
                <input
                  ref={colorInputRef}
                  type="color"
                  value={backgroundColor}
                  onChange={(e) => {
                  setBackgroundColor(e.target.value)
                  trackColorChange('background', e.target.value)
                }}
                  className="w-full h-10"
                />
                
                {/* Hex Color Display - Clickable to copy */}
                <div 
                  className="absolute inset-0 flex items-center justify-center pointer-events-none"
                >
                  <span 
                    className={`font-mono text-sm font-medium cursor-pointer pointer-events-auto select-none transition-all duration-200 hover:scale-110 ${isColorDark(backgroundColor) ? 'text-white' : 'text-gray-600'}`}
                    onClick={() => {
                      navigator.clipboard.writeText(backgroundColor).then(() => {
                        setShowToast(true)
                        setTimeout(() => setShowToast(false), 3000) // Hide after 3 seconds
                      }).catch(err => {
                        console.error('Failed to copy color:', err)
                      })
                    }}
                    title="Click to copy hex color"
                  >
                    {backgroundColor.toUpperCase()}
                  </span>
                </div>
                
                <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                  <Pipette className={`w-4 h-4 ${isColorDark(backgroundColor) ? 'text-white' : 'text-gray-500'}`} />
                </div>
              </div>
            </div>

            {/* Customize Options - More compact spacing */}
            <div>
              <div className="space-y-4 flex-1 overflow-y-auto">
                {/* App Name */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Android File Name:
                  </label>
                  <input
                    type="text"
                    value={iconName}
                    onChange={(e) => setIconName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-primary-500 text-sm"
                    placeholder="ic_launcher"
                  />
                </div>
              </div>
            </div>

            {/* Advanced Customization Section - More compact */}
            <div className="border-t border-b border-gray-200 pt-3 pb-3">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full flex items-center justify-between text-sm font-semibold text-gray-700 hover:text-gray-900"
              >
                <div className="flex flex-col items-start space-y-2">
                  <span>Advanced Customization</span>
                  <div className="flex space-x-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      Badge
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Monochrome
                    </span>
                  </div>
                </div>
                {showAdvanced ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </button>

              {showAdvanced && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  className="mt-5 space-y-3"
                >
                  {/* Badge Section */}
                  <div className="gap-5 flex flex-col border-b border-gray-100 pb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={showBadge}
                            onChange={(e) => setShowBadge(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                        <span className="text-sm font-medium text-gray-700">Show Badge</span>
                      </div>
                    </div>
                    
                    {showBadge && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-3"
                      >
                        {/* Badge Text Input */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">
                            Badge Text:
                          </label>
                          <input
                            type="text"
                            value={badgeText}
                            onChange={(e) => setBadgeText(e.target.value)}
                            placeholder="Enter badge text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:border-primary-500 text-sm"
                          />
                        </div>

                        {/* Badge Color Picker */}
                        <div>
                          <label className="block text-xs font-semibold text-gray-700 mb-1">
                            Badge Color
                          </label>
                          <div className="relative h-10">
                            <input
                              type="color"
                              value={badgeColor}
                              onChange={(e) => {
                              setBadgeColor(e.target.value)
                              trackColorChange('badge', e.target.value)
                            }}
                              className="w-full h-10 rounded-lg cursor-pointer"
                            />
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <span 
                                className={`font-mono text-sm font-medium cursor-pointer pointer-events-auto select-none transition-all duration-200 hover:scale-110 ${isColorDark(badgeColor) ? 'text-white' : 'text-gray-600'}`}
                                onClick={() => {
                                  navigator.clipboard.writeText(badgeColor).then(() => {
                                    setShowToast(true)
                                    setTimeout(() => setShowToast(false), 3000)
                                  }).catch(err => {
                                    console.error('Failed to copy color:', err)
                                  })
                                }}
                                title="Click to copy hex color"
                              >
                                {badgeColor.toUpperCase()}
                              </span>
                            </div>
                            <div className="absolute inset-y-0 right-3 flex items-center pointer-events-none">
                              <Pipette className={`w-4 h-4 ${isColorDark(badgeColor) ? 'text-white' : 'text-gray-500'}`} />
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>

                  {/* Monochrome Section */}
                  <div className="gap-3 flex flex-col">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={showMonochrome}
                            onChange={(e) => setShowMonochrome(e.target.checked)}
                            className="sr-only peer"
                          />
                          <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-600"></div>
                        </label>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-medium text-gray-700">Android Monochrome Icon</span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Android 14+
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {showMonochrome && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-3"
                      >
                        <p className="text-xs text-gray-500">
                          Upload a monochrome version for themed Android icons. Should be white on transparent background.
                        </p>
                    
                    <motion.div
                      {...getMonochromeRootProps()}
                      className={`
                        border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-all duration-300 shadow-sm
                        ${isMonochromeDragActive 
                          ? 'border-green-500 bg-green-50 shadow-md ring-2 ring-green-200' 
                          : monochromeImage
                            ? 'border-green-400 bg-green-50/50 hover:border-green-500 hover:bg-green-50'
                            : 'border-gray-300 bg-gray-50 hover:border-green-400 hover:bg-green-50 hover:shadow-md'
                        }
                      `}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <input {...getMonochromeInputProps()} />
                      {monochromeImage ? (
                        <div className="flex items-center justify-center space-x-3">
                          <div className="w-6 h-6 rounded-lg overflow-hidden flex-shrink-0 ring-2 ring-green-200 bg-black">
                            <img src={monochromeImage} alt="Monochrome" className="w-full h-full object-cover" />
                          </div>
                          <div className="flex items-center space-x-2">
                            <CheckCircle2 className="w-3 h-3 text-green-500" />
                            <span className="text-green-600 font-medium text-xs">Click to change</span>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-2">
                          <div className="w-6 h-6 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <Upload className="w-4 h-4 text-gray-600" />
                          </div>
                          <div>
                            <div className="text-gray-700 font-medium text-xs mb-0.5">Upload monochrome</div>
                            <div className="text-xs text-gray-500">White on transparent PNG</div>
                          </div>
                        </div>
                      )}
                    </motion.div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              )}
            </div>

            <div style={{ minWidth: '300px', minHeight: '200px', width: '100%' }}>
              <GoogleAd 
                slot="2466299289" 
                style={{ display: 'block', minWidth: '300px', width: '100%', minHeight: '200px' }}
                format="auto"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Right Column - Banner, Title, Download & Preview */}
      <div className="lg:col-span-2 flex flex-col pb-8 overflow-hidden relative min-h-[calc(100vh-2rem)] h-[calc(100vh-32px)]">
        <div className="text-center mb-6">
          <img 
            src={bannerLogo} 
            alt="EasyAppIcon Banner" 
            className="mx-auto h-20 md:h-24 lg:h-32 w-auto"
          />
        </div>

        {/* Banner and Title - Animated out when image uploaded */}
        <motion.div
          className="flex flex-col"
          initial={{ opacity: 1, y: 0 }}
          animate={{ 
            opacity: uploadedImage ? 0 : 1, 
            height: uploadedImage ? 0 : 'auto'
          }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          style={{ overflow: 'hidden' }}
        >
          {/* Updated Subtitle */}
          <div className="text-center mb-6">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 leading-tight">
              Generate your app icon easily.
            </h1>
            <p className="text-lg md:text-xl text-gray-600 font-medium mb-4">
              iOS + Adaptive Android Icon
            </p>
            {/* Donation and Support Buttons */}
            <div className="flex justify-center items-center space-x-4">
              {/* Donation Button */}
              <a 
                href="https://www.buymeacoffee.com/dg43aLHr6" 
                target="_blank" 
                rel="noopener noreferrer"
                onClick={() => trackDonationClick()}
              >
                <img 
                  src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png" 
                  alt="Buy Me A Coffee" 
                  className="h-10 w-auto hover:scale-105 transition-transform duration-200"
                />
              </a>

              {/* Brand Logo Button */}
              <a
                href="https://easyio.dev"
                target="_blank"
                rel="noopener noreferrer"
                className="group h-10 px-3 bg-black rounded-lg flex items-center justify-center transition-all duration-300"
              >
                <img
                  src={brandLogo}
                  alt="EasyAppIcon"
                  className="h-6 w-auto"
                />
                <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 group-hover:max-w-[150px] group-hover:opacity-100 group-hover:ml-2 transition-all duration-500 ease-out text-white font-semibold text-sm">
                  Built by EasyIO.dev
                </span>
              </a>
              
              {/* Email Button */}
              <a 
                href="mailto:supports@easyappicon.com" 
                onClick={() => trackEmailClick()}
                className="h-10 px-4 bg-white hover:bg-gray-50 text-gray-700 hover:text-gray-900 font-semibold text-sm rounded-lg flex items-center gap-2 hover:scale-105 transition-all duration-200 border border-gray-200 hover:border-gray-300"
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#df6e57' }}>
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
              </a>
              
              {/* Old Version Button
              <a 
                href="https://deprecated.easyappicon.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="h-10 px-4 bg-white hover:bg-gray-50 text-gray-700 hover:text-gray-900 font-semibold text-sm rounded-lg flex items-center gap-2 hover:scale-105 transition-all duration-200 border border-gray-200 hover:border-gray-300"
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" style={{ color: '#6b7280' }}>
                  <path d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0 0 13 21a9 9 0 0 0 0-18zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"/>
                </svg>
              </a>
              */}
            </div>
          </div>
        </motion.div> 

        {/* Live Preview with Download Button - Animated to full height */}
        {uploadedImage ? (
          <motion.div 
            className="bg-white rounded-xl shadow-soft border border-gray-200 p-5 pb-0 overflow-y-hidden flex-1"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
          >
            {/* Header with Download Button and Output Format on same line */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-2">
                <Smartphone className="w-5 h-5 text-gray-600" />
                <h3 className="text-xl font-semibold text-gray-900">Live Preview</h3>
              </div>
              <div className="flex items-center space-x-3">
                {/* Output Format Toggle */}
                <div className="flex flex-col items-end space-y-1">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 font-medium">Format:</span>
                    <div className="flex bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setOutputFormat('png')}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                          outputFormat === 'png'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <div className="flex items-center space-x-1">
                          <span className="font-semibold">PNG</span>
                        </div>
                      </button>
                      <button
                        onClick={() => setOutputFormat('webp')}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                          outputFormat === 'webp'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <div className="flex items-center space-x-1">
                          <span className="font-semibold">WebP</span>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
                <motion.button
                  onClick={() => {
                    setShowDownloadModal(true)
                    handleGenerate(outputFormat)
                    
                    // Track icon generation
                    trackIconGeneration('desktop', backgroundColor !== '#ffffff', showBadge)
                    trackDownload('icon_package')
                  }}
                  disabled={isGenerating}
                  className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 disabled:from-gray-400 disabled:to-gray-500 text-white px-6 py-2 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center space-x-2 shadow-lg"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {isGenerating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      <span>Download</span>
                    </>
                  )}
                </motion.button>
              </div>
            </div>

            {/* Icon Types Row */}
            <div className="mb-10">
              <div className="grid grid-cols-3 gap-8">
                {iconTypes.map((iconType) => (
                  <div key={iconType.name} className="text-center">
                    <div className="mb-4">
                      <div 
                        className={`mx-auto shadow-lg overflow-hidden ${iconType.style} relative`}
                        style={{ 
                          width: 100, 
                          height: 100,
                          backgroundColor: backgroundColor 
                        }}
                      >
                        <div 
                          className="w-full h-full flex items-center justify-center"
                          style={{ 
                            padding: padding > 0 ? `${padding / 2}%` : '0',
                            transform: padding < 0 ? `scale(${1 + Math.abs(padding) / 100})` : 'none'
                          }}
                        >
                          <img 
                            src={uploadedImage} 
                            alt="Icon preview" 
                            className="w-full h-full object-contain"
                          />
                        </div>
                        {showBadge && badgeText && (
                          <div 
                            className="absolute bottom-0 left-0 right-0 text-white text-xs py-1 px-2 text-center"
                            style={{ backgroundColor: badgeColor }}
                          >
                            {badgeText}
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 font-medium">{iconType.name}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Device Mockup Previews - Half Height */}
            <div>
              <div className="grid grid-cols-2 gap-12">
                {/* iPhone Mockup - Half Height */}
                <div className="text-center">
                  <div className="relative mx-auto w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-xs xl:max-w-sm" style={{ minWidth: '260px' }}>
                    {/* iPhone Background Mockup - Cropped to top half */}
                    <div className="relative w-full overflow-hidden">
                      <img 
                        src={iphoneMockup} 
                        alt="iPhone Mockup" 
                        className="w-full object-contain filter drop-shadow-2xl"
                        style={{ 
                          imageRendering: 'crisp-edges',
                          height: '200%',
                          objectPosition: 'top'
                        }}
                      />
                      
                      {/* App Icon + Name Container - Combined */}
                      {uploadedImage && (
                        <div 
                          className="absolute z-10 flex flex-col justify-center items-center"
                          style={{ 
                            top: '12%', 
                            left: '12%'
                          }}
                        >
                          {/* App Icon */}
                          <div 
                            className="overflow-hidden shadow-lg relative"
                            style={{ 
                              width: '46px', 
                              height: '46px',
                              backgroundColor: backgroundColor,
                              borderRadius: '10px',
                            }}
                          >
                            <div 
                              className="w-full h-full flex items-center justify-center"
                              style={{ 
                                padding: padding > 0 ? `${padding / 2}%` : '0',
                                transform: padding < 0 ? `scale(${1 + Math.abs(padding) / 100})` : 'none'
                              }}
                            >
                              <img 
                                src={uploadedImage} 
                                alt="App icon preview" 
                                className="w-full h-full object-contain"
                                style={{ imageRendering: 'crisp-edges' }}
                              />
                            </div>
                            {showBadge && badgeText && (
                              <div 
                                className="absolute bottom-0 left-0 right-0 text-white text-[6px] py-0.5 pb-0 px-1 text-center"
                                style={{ backgroundColor: badgeColor }}
                              >
                                {badgeText}
                              </div>
                            )}
                          </div>
                          
                          {/* App Name - Relative to icon container */}
                          <div className="mt-1 flex justify-center">
                            <p className="text-white font-medium text-center whitespace-nowrap select-none" style={{ 
                              fontSize: '9px', 
                              textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                              filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))',
                            }}>
                              [App name]
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Pixel Phone Mockup - Half Height */}
                <div className="text-center">
                  <div className="relative mx-auto w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-xs xl:max-w-sm" style={{ minWidth: '260px' }}>
                    {/* Pixel Phone Background Mockup - Cropped to top half */}
                    <div className="relative w-full overflow-hidden">
                      <img 
                        src={pixelMockup} 
                        alt="Pixel Phone Mockup" 
                        className="w-full object-contain filter drop-shadow-2xl"
                        style={{ 
                          imageRendering: 'crisp-edges',
                          height: '200%',
                          objectPosition: 'top'
                        }}
                      />
                      
                      {/* App Icon + Name Container - Combined */}
                      {uploadedImage && (
                        <div 
                          className="absolute z-10 flex flex-col justify-center items-center"
                          style={{ 
                            top: '12%', 
                            left: '12%'
                          }}
                        >
                          {/* Regular App Icon */}
                          <div 
                            className="overflow-hidden shadow-lg relative"
                            style={{ 
                              width: '46px', 
                              height: '46px',
                              backgroundColor: backgroundColor,
                              borderRadius: '7px',
                            }}
                          >
                            <div 
                              className="w-full h-full flex items-center justify-center"
                              style={{ 
                                padding: padding > 0 ? `${padding / 2}%` : '0',
                                transform: padding < 0 ? `scale(${1 + Math.abs(padding) / 100})` : 'none'
                              }}
                            >
                              <img 
                                src={uploadedImage} 
                                alt="App icon preview" 
                                className="w-full h-full object-contain"
                                style={{ imageRendering: 'crisp-edges' }}
                              />
                            </div>
                            {showBadge && badgeText && (
                              <div 
                                className="absolute bottom-0 left-0 right-0 text-white text-[6px] py-0.5 pb-0 px-1 text-center"
                                style={{ backgroundColor: badgeColor }}
                              >
                                {badgeText}
                              </div>
                            )}
                          </div>
                          
                          {/* App Name - Relative to icon container */}
                          <div className="mt-1 flex justify-center">
                            <p className="text-white font-medium text-center whitespace-nowrap select-none" style={{ 
                              fontSize: '9px', 
                              textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                              filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))',
                            }}>
                              [App name]
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Monochrome/Themed Icon Previews - Next line below original icon */}
                      {showMonochrome && monochromeImage && uploadedImage && (
                        <div 
                          className="absolute z-10"
                          style={{ 
                            top: '22%', 
                            left: '12%'
                          }}
                        >
                          {/* Monochrome Preview Label */}
                          <div className="mb-2">
                            <div className="bg-black/70 backdrop-blur-sm rounded-full px-2 py-1 flex items-center space-x-1">
                              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                              <span className="text-white text-xs font-medium">Themed Icons Demo</span>
                            </div>
                          </div>
                          
                          {/* Themed Icons Row */}
                          <div className="flex space-x-3">
                          {/* Blue Themed Icon */}
                          <div className="flex flex-col items-center">
                            <div 
                              className="overflow-hidden shadow-lg relative"
                              style={{ 
                                width: '46px', 
                                height: '46px',
                                backgroundColor: '#3b82f6', // Blue themed background
                                borderRadius: '7px',
                              }}
                            >
                              <div 
                                className="w-full h-full flex items-center justify-center"
                                style={{ 
                                  padding: padding > 0 ? `${padding / 2}%` : '0',
                                  transform: padding < 0 ? `scale(${1 + Math.abs(padding) / 100})` : 'none'
                                }}
                              >
                                <img 
                                  src={monochromeImage} 
                                  alt="Blue themed icon" 
                                  className="w-full h-full object-contain"
                                  style={{ 
                                    imageRendering: 'crisp-edges',
                                    filter: 'brightness(0) invert(1)' // Make monochrome white
                                  }}
                                />
                              </div>
                              {showBadge && badgeText && (
                                <div 
                                  className="absolute bottom-0 left-0 right-0 text-white text-[6px] py-0.5 pb-0 px-1 text-center"
                                  style={{ backgroundColor: badgeColor }}
                                >
                                  {badgeText}
                                </div>
                              )}
                            </div>
                            <p className="text-white text-center whitespace-nowrap select-none mt-1" style={{ 
                              fontSize: '9px', 
                              textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                              filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))',
                            }}>
                              Blue
                            </p>
                          </div>

                          {/* Green Themed Icon */}
                          <div className="flex flex-col items-center">
                            <div 
                              className="overflow-hidden shadow-lg relative"
                              style={{ 
                                width: '46px', 
                                height: '46px',
                                backgroundColor: '#10b981', // Green themed background
                                borderRadius: '7px',
                              }}
                            >
                              <div 
                                className="w-full h-full flex items-center justify-center"
                                style={{ 
                                  padding: padding > 0 ? `${padding / 2}%` : '0',
                                  transform: padding < 0 ? `scale(${1 + Math.abs(padding) / 100})` : 'none'
                                }}
                              >
                                <img 
                                  src={monochromeImage} 
                                  alt="Green themed icon" 
                                  className="w-full h-full object-contain"
                                  style={{ 
                                    imageRendering: 'crisp-edges',
                                    filter: 'brightness(0) invert(1)' // Make monochrome white
                                  }}
                                />
                              </div>
                              {showBadge && badgeText && (
                                <div 
                                  className="absolute bottom-0 left-0 right-0 text-white text-[6px] py-0.5 pb-0 px-1 text-center"
                                  style={{ backgroundColor: badgeColor }}
                                >
                                  {badgeText}
                                </div>
                              )}
                            </div>
                            <p className="text-white text-center whitespace-nowrap select-none mt-1" style={{ 
                              fontSize: '9px', 
                              textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                              filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))',
                            }}>
                              Green
                            </p>
                          </div>

                          {/* Purple Themed Icon */}
                          <div className="flex flex-col items-center">
                            <div 
                              className="overflow-hidden shadow-lg relative"
                              style={{ 
                                width: '46px', 
                                height: '46px',
                                backgroundColor: '#8b5cf6', // Purple themed background
                                borderRadius: '7px',
                              }}
                            >
                              <div 
                                className="w-full h-full flex items-center justify-center"
                                style={{ 
                                  padding: padding > 0 ? `${padding / 2}%` : '0',
                                  transform: padding < 0 ? `scale(${1 + Math.abs(padding) / 100})` : 'none'
                                }}
                              >
                                <img 
                                  src={monochromeImage} 
                                  alt="Purple themed icon" 
                                  className="w-full h-full object-contain"
                                  style={{ 
                                    imageRendering: 'crisp-edges',
                                    filter: 'brightness(0) invert(1)' // Make monochrome white
                                  }}
                                />
                              </div>
                              {showBadge && badgeText && (
                                <div 
                                  className="absolute bottom-0 left-0 right-0 text-white text-[6px] py-0.5 pb-0 px-1 text-center"
                                  style={{ backgroundColor: badgeColor }}
                                >
                                  {badgeText}
                                </div>
                              )}
                            </div>
                            <p className="text-white text-center whitespace-nowrap select-none mt-1" style={{ 
                              fontSize: '9px', 
                              textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                              filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))',
                            }}>
                              Purple
                            </p>
                          </div>

                          {/* Dark Themed Icon */}
                          <div className="flex flex-col items-center">
                            <div 
                              className="overflow-hidden shadow-lg relative"
                              style={{ 
                                width: '46px', 
                                height: '46px',
                                backgroundColor: '#1f2937', // Dark themed background
                                borderRadius: '7px',
                              }}
                            >
                              <div 
                                className="w-full h-full flex items-center justify-center"
                                style={{ 
                                  padding: padding > 0 ? `${padding / 2}%` : '0',
                                  transform: padding < 0 ? `scale(${1 + Math.abs(padding) / 100})` : 'none'
                                }}
                              >
                                <img 
                                  src={monochromeImage} 
                                  alt="Dark themed icon" 
                                  className="w-full h-full object-contain"
                                  style={{ 
                                    imageRendering: 'crisp-edges',
                                    filter: 'brightness(0) invert(1)' // Make monochrome white
                                  }}
                                />
                              </div>
                              {showBadge && badgeText && (
                                <div 
                                  className="absolute bottom-0 left-0 right-0 text-white text-[6px] py-0.5 pb-0 px-1 text-center"
                                  style={{ backgroundColor: badgeColor }}
                                >
                                  {badgeText}
                                </div>
                              )}
                            </div>
                            <p className="text-white text-center whitespace-nowrap select-none mt-1" style={{ 
                              fontSize: '9px', 
                              textShadow: '0 1px 3px rgba(0,0,0,0.4)',
                              filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))',
                            }}>
                              Dark
                            </p>
                          </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Preview Labels - Bottom of right panel */}
            <div className="absolute bottom-12 left-0 right-0 grid grid-cols-2 gap-12 px-5">
              {/* iPhone Preview Label - Left Column */}
              <div className="flex flex-row items-center justify-center space-x-2">
                <div className="flex flex-row items-center justify-center space-x-2 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 shadow-sm">
                  <Apple className="w-4 h-4 text-gray-600" />
                  <p className="text-sm text-gray-600 font-medium">iPhone Preview</p>
                </div>
              </div>
              
              {/* Android Preview Label - Right Column */}
              <div className="flex flex-row items-center justify-center space-x-2">
                <div className="flex flex-row items-center justify-center space-x-2 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 shadow-sm">
                  <RiAndroidLine className="w-4 h-4 text-gray-600" />
                  <p className="text-sm text-gray-600 font-medium">Android Preview</p>
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div 
            {...getPreviewRootProps()}
            className={`
              rounded-xl border-2 border-dashed p-16 text-center flex-1 flex items-center justify-center cursor-pointer transition-all duration-300
              ${isPreviewDragActive 
                ? 'border-primary-400 bg-primary-50' 
                : 'border-gray-300 bg-gray-50 hover:border-primary-400 hover:bg-primary-25'
              }
            `}
            initial={{ opacity: 1 }}
            animate={{ opacity: uploadedImage ? 0 : 1 }}
            transition={{ duration: 0.3 }}
            whileTap={{ scale: 0.99 }}
          >
            <input {...getPreviewInputProps()} />
            <div className="max-w-sm mx-auto">
              <div className="flex space-x-4 mb-6 justify-center">
                {isPreviewDragActive ? (
                  <Upload className="w-12 h-12 text-primary-500 animate-bounce" />
                ) : (
                  <>
                    <Apple className="w-12 h-12 text-gray-400" />
                    <RiAndroidLine className="w-12 h-12 text-gray-400" />
                  </>
                )}
              </div>
              <h3 className={`text-xl font-medium mb-2 ${isPreviewDragActive ? 'text-primary-700' : 'text-gray-700'}`}>
                {isPreviewDragActive ? 'Drop your image here!' : 'Ready to Create?'}
              </h3>
              <p className={`${isPreviewDragActive ? 'text-primary-600' : 'text-gray-500'}`}>
                {isPreviewDragActive 
                  ? 'Release to upload your foreground image'
                  : 'Upload an image to see live previews for iOS and Android'
                }
              </p>
              {!isPreviewDragActive && (
                <p className="text-sm text-gray-400 mt-2">
                  Or drag & drop your image anywhere here
                </p>
              )}
            </div>
          </motion.div>
        )}
      </div>

      {/* Toast Notification */}
      {showToast && (
        <motion.div
          className="fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50"
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 50, scale: 0.9 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          <CheckCircle2 className="w-4 h-4" />
          <span className="font-medium">Copied to clipboard!</span>
        </motion.div>
      )}

      {/* Download Modal */}
      {showDownloadModal && (
        <motion.div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full mx-4 overflow-hidden"
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            {/* Modal Header with Close Button */}
            <div className="flex justify-between items-start p-4 pb-0">
              <div></div> {/* Empty div for spacing */}
              <button
                onClick={() => setShowDownloadModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors duration-200 flex-shrink-0"
                title="Close"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="px-8 pb-8">
              <div className="flex items-center space-x-8 mb-0">
                {/* Dancing Kitty - Much Larger */}
                <div className="flex-shrink-0">
                  <img 
                    src={dancingKitty} 
                    alt="Dancing Kitty" 
                    className="w-48 h-48 object-contain"
                  />
                </div>
                
                {/* Text Content */}
                <div className="flex flex-col" >
                  <h2 className="text-3xl font-bold text-gray-900 mb-2">
                    Thank you for using our services!
                  </h2>
                  <p className="text-gray-600 text-lg">
                    Downloading your icons now.
                  </p>
                </div>
              </div>
            </div>

            {/* Help Request Text */}
            <div className="px-8 py-4 text-center border-t border-gray-200">
              <p className="text-gray-600 text-sm">
                If you found this tool helpful, please consider supporting us! Your support helps us keep this service free for everyone. 🙏
              </p>
            </div>

            {/* Button Row */}
            <div className="bg-gray-50 px-8 py-4 flex justify-between items-center">
              {/* Left Side Buttons */}
              <div className="flex space-x-3 items-center">
                {/* Donation Button */}
                <div className="flex-shrink-0">
                  <a 
                    href="https://www.buymeacoffee.com/dg43aLHr6" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    onClick={() => trackDonationClick()}
                  >
                    <img 
                      src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png" 
                      alt="Buy Me A Coffee" 
                      className="h-10 w-auto hover:scale-105 transition-transform duration-200"
                    />
                  </a>
                </div>
              </div>
              
              {/* Action Buttons - Right Side */}
              <div className="flex space-x-3 items-center">
                <button
                  onClick={() => {
                    setShowDownloadModal(false)
                    window.location.reload();
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors duration-200"
                >
                  Start Over
                </button>
                <button
                  onClick={() => {
                    setShowDownloadModal(false)
                    window.location.href = '/support-us'
                  }}
                  className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors duration-200"
                >
                  Give us some help
                </button>
                {/* Brand Logo Button */}
                <a
                  href="https://easyio.dev"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group h-10 px-3 bg-black rounded-lg flex items-center justify-center transition-all duration-300"
                >
                  <img
                    src={brandLogo}
                    alt="EasyAppIcon"
                    className="h-6 w-auto"
                  />
                  <span className="max-w-0 overflow-hidden whitespace-nowrap opacity-0 group-hover:max-w-[150px] group-hover:opacity-100 group-hover:ml-2 transition-all duration-500 ease-out text-white font-semibold text-sm">
                    Built by EasyIO.dev
                  </span>
                </a>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      </div>
    </div>
  )
}

export default DesktopIconGenerator 